import { useState } from 'react';
import { instructors } from '../../../mocks/instructors';
import { useElementScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function InstructorsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useElementScrollAnimation();
  const instructor = instructors[activeIndex];

  const handlePrev = () => setActiveIndex((prev) => (prev === 0 ? instructors.length - 1 : prev - 1));
  const handleNext = () => setActiveIndex((prev) => (prev === instructors.length - 1 ? 0 : prev + 1));

  return (
    <section id="instructors" className="py-24 bg-black" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 animate-on-scroll">
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase mb-4 block">Instructors</span>
          <h2 className="font-serif-kr font-black text-white text-4xl md:text-5xl">전문 강사진</h2>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Quote + Nav */}
          <div className="animate-on-scroll-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-800 border border-neutral-700 mb-8">
              <span className="text-white text-xs font-semibold">{instructor.title}</span>
              <i className="ri-star-fill text-amber-400 text-xs" />
            </div>

            {/* Quote */}
            <blockquote className="relative mb-8">
              <i className="ri-double-quotes-l text-red-800/30 text-7xl absolute -top-4 -left-2" />
              <p className="font-serif-kr text-neutral-200 text-lg md:text-xl leading-relaxed pl-4 relative z-10">
                {instructor.quote}
              </p>
            </blockquote>

            {/* Instructor Info */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-red-700">
                  <img
                    src={instructor.image}
                    alt={instructor.name}
                    className="w-full h-full object-cover"
                    style={{ transform: instructor.imageTransform, transformOrigin: 'top center' }}
                  />
                </div>
                <div>
                  <div className="font-serif-kr font-bold text-white text-lg">{instructor.name}</div>
                  <div className="text-neutral-500 text-xs mt-0.5">
                    {instructor.belt}{instructor.experience ? ` · ${instructor.experience}` : ''}
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex gap-2">
                <button
                  onClick={handlePrev}
                  className="w-10 h-10 rounded-lg flex items-center justify-center border border-neutral-700 bg-neutral-900 hover:bg-neutral-800 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-neutral-300" />
                </button>
                <button
                  onClick={handleNext}
                  className="w-10 h-10 rounded-lg flex items-center justify-center bg-white text-black hover:bg-neutral-200 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>

            {/* Dot Indicators */}
            <div className="flex gap-2 mt-6">
              {instructors.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                    i === activeIndex ? 'w-8 bg-red-600' : 'w-1.5 bg-neutral-700'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right: Profile Card */}
          <div className="animate-on-scroll-right animate-delay-200">
            <div className="relative">
              {/* Main Photo */}
              <div className="w-full h-[480px] rounded-2xl overflow-hidden">
                <img
                  src={instructor.image}
                  alt={instructor.name}
                  className="w-full h-full object-cover transition-all duration-500"
                  style={{ transform: instructor.imageTransform, transformOrigin: 'top center' }}
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              </div>

              {/* Overlay Info */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                  <h3 className="font-serif-kr font-bold text-white text-xl mb-1">{instructor.name}</h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {instructor.specialties.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-white/20 text-white text-xs">
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 space-y-1">
                    {instructor.certifications.map((cert, i) => (
                      <div key={i} className="flex items-center gap-2 text-white/70 text-xs">
                        <i className="ri-checkbox-circle-line text-red-400" />
                        {cert}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* All Instructors Grid */}
        <div className="mt-16 grid grid-cols-3 gap-4 animate-on-scroll animate-delay-300">
          {instructors.map((inst, i) => (
            <button
              key={inst.id}
              onClick={() => setActiveIndex(i)}
              className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-300 cursor-pointer text-left ${
                i === activeIndex
                  ? 'bg-neutral-800 border border-neutral-600'
                  : 'bg-neutral-900 border border-neutral-800 hover:border-neutral-600'
              }`}
            >
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                <img src={inst.image} alt={inst.name} className="w-full h-full object-cover" style={{ transform: inst.imageTransform, transformOrigin: 'top center' }} />
              </div>
              <div>
                <div className="font-medium text-sm text-white">
                  {inst.name}
                </div>
                <div className={`text-xs mt-0.5 ${i === activeIndex ? 'text-neutral-400' : 'text-neutral-500'}`}>
                  {inst.belt}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
